import React, { useEffect, useState, useMemo, useCallback } from "react";
import { Grid, Alert, Button, Tooltip, Stack } from "@mui/material";
import { IHotel } from "@vmosolutions/oc-interfaces";
import "react-datepicker/dist/react-datepicker.css";
import Bookings from "./Bookings/Bookings";
import CachedIcon from "@mui/icons-material/Cached";
import { useSelector, useDispatch } from "react-redux";
import { IRootState } from "../../../redux/reducers/CombinedReducers";
import { SelectField } from "src/components/MUI5FormikComponents/SelectField";
import { DateTimeField } from "src/components/MUI5FormikComponents/DateTimeField";
import { getHotelBookingsThunk, setFilter } from "../../../redux/reducers/HotelPortalReducer";
import AmplifyAPIWrapper from "../../../API/AmplifyAPIWrapper";

interface IHotelOptions {
  value: string;
  label: string;
}

interface IHotelDashboardProps {
  tableMaxHeight: number;
}

function HotelBookingDashboard(props: IHotelDashboardProps): JSX.Element {
  const { tableMaxHeight } = props;
  const statusMsg = useSelector((state: IRootState) => state.hotelBookings.statusMsg);
  const fetchingThunk = useSelector((state: IRootState) => state.hotelBookings.fetchingThunk);

  const [startDate, setStartDate] = useState<Date>(new Date());
  const initialEnd = new Date();
  const [endDate, setEndDate] = useState<Date>(new Date(initialEnd.setDate(initialEnd.getDate() + 45)));
  const [selectedHotels, setSelectedHotels] = useState<IHotelOptions[]>([]);
  const [hotels, setHotels] = useState<IHotel[]>([]);
  const [hotelListLoaded, setHotelListLoaded] = useState<boolean>(false);
  const dispatch = useDispatch();

  const startEpoch: number = useMemo(() => {
    return startDate.valueOf();
  }, [startDate]);

  const endEpoch: number = useMemo(() => {
    return endDate.valueOf();
  }, [endDate]);

  useEffect(() => {
    const getHotelsAction = async (): Promise<void> => {
      const response = await AmplifyAPIWrapper.Get<IHotel[]>("/hotels");
      if (Array.isArray(response)) {
        setHotels(response);
        setHotelListLoaded(true);
      }
    };
    getHotelsAction();
  }, [dispatch]);

  const handleRefresh = (): void => {
    if (!fetchingThunk) {
      dispatch(
        getHotelBookingsThunk({
          hotelIDs: selectedHotels.map((hotel) => hotel.value),
          start: startEpoch,
          end: endEpoch,
        })
      );
    }
  };
  const updateFilter = (filter: string): void => {
    dispatch(setFilter(filter));
  };
  const clearFilter = (): void => {
    dispatch(setFilter(""));
  };

  useEffect(() => {
    if (hotelListLoaded && selectedHotels.length > 0) {
      handleRefresh();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hotelListLoaded]);

  const hotelOptions = useMemo((): IHotelOptions[] => {
    return hotels.map((hotel) => {
      return { label: hotel.name, value: hotel._id };
    });
  }, [hotels]);

  const handleHotelSelect = useCallback((newValues: any) => {
    setSelectedHotels(newValues);
  }, []);

  const animate = !fetchingThunk
    ? {}
    : {
        animation: "spin 2s linear infinite reverse",
        "@keyframes spin": {
          "0%": {
            transform: "rotate(0deg)",
          },
          "100%": {
            transform: "rotate(360deg)",
          },
        },
      };
  return (
    <Grid container item direction="row" md={12} sx={{ p: 1 }}>
      <Grid container item xs={12} alignItems="center">
        <Grid item xs={4}>
          <SelectField
            name="hotelIDs"
            options={hotelOptions}
            isMulti={true}
            isSearchable={true}
            isClearable={true}
            value={selectedHotels}
            onChange={handleHotelSelect}
            placeholder="Select Hotels..."
            //multiMode="checkbox"
          />
        </Grid>
        <Grid container item xs={4}>
          <Grid item xs={6}>
            <DateTimeField
              name="starttime"
              hideTimeControl
              value={startDate.toISOString()}
              label="Start Date"
              onChange={(date) => setStartDate(new Date(date))}
            />
          </Grid>
          <Grid item xs={6}>
            <DateTimeField
              name="endtime"
              hideTimeControl
              value={endDate.toISOString()}
              label="End Date"
              onChange={(date) => setEndDate(new Date(date))}
            />
          </Grid>
        </Grid>
        <Grid item xs={1} sx={{ pl: 2 }}>
          <Tooltip title="Get Hotel Bookings List">
            <Button variant="contained" color="primary" onClick={() => handleRefresh()}>
              <CachedIcon fontSize="small" sx={{ ...animate }} />
            </Button>
          </Tooltip>
        </Grid>
        <Grid item xs={3}>
          {statusMsg && (
            <Alert sx={{ py: 0, px: 2 }} severity={statusMsg.severity ?? "info"} variant="filled">
              {statusMsg.msg}
            </Alert>
          )}
        </Grid>
        <Grid item xs={12} sx={{ pt: 1 }}>
          {selectedHotels.length > 0 ? (
            <Bookings updateFilter={updateFilter} tableMaxHeight={tableMaxHeight} />
          ) : (
            <Alert
              severity="info"
              variant="filled"
              sx={{
                display: "flex",
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              <Stack direction="row" alignItems="center" gap={1}>
                <span>Select hotels and press</span>
                <CachedIcon />
                <span>to get started</span>
              </Stack>
            </Alert>
          )}
        </Grid>
      </Grid>
    </Grid>
  );
}

export default HotelBookingDashboard;
